import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_9c6a7b9b-6c36-4fd3-a6ee-0e90154b575f/artifacts/5l6igzsb_IMG_9847.PNG";

const links = [
  { to: "/#about", label: "About" },
  { to: "/restaurants", label: "Restaurants" },
  { to: "/analytics", label: "Analytics" },
  { to: "/#contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-gradient-to-b from-black/70 to-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <img src={LOGO} alt="FUDK" className="h-9 w-9 md:h-10 md:w-10 object-contain rounded-md bg-black ring-1 ring-white/10" />
          <div className="leading-none">
            <div className="font-display font-black text-white text-xl md:text-2xl tracking-tight">FUDK</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-lime-400/90">Food Delivery</div>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive && !l.to.includes("#") ? "text-lime-400" : "text-white/85 hover:text-lime-400"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href="/#download"
            data-testid="nav-cta-download"
            className="ml-2 inline-flex items-center rounded-full bg-lime-DEFAULT bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold text-sm px-5 py-2.5 transition-colors"
          >
            Get the app
          </a>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2 rounded-md hover:bg-white/10"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-black/95 border-t border-white/10">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className="text-white/90 hover:text-lime-400 py-2 text-base"
              >
                {l.label}
              </Link>
            ))}
            <a
              href="/#download"
              data-testid="nav-mobile-cta-download"
              className="mt-2 inline-flex justify-center rounded-full bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold text-sm px-5 py-3"
            >
              Get the app
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
